<!-- ABOUTME: Email automation campaign cycles viewer with expandable skipped emails -->
<!-- ABOUTME: Shows grouped email campaigns by template and date with simple timeframe toggle -->
<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header Section -->
    <header class="bg-white border-b border-gray-200 py-5 px-5">
      <div class="max-w-7xl mx-auto">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 class="text-3xl font-bold text-title-dark m-0">Email Campaign Cycles</h1>
            <p class="text-text-dark text-sm mt-1 mb-0">View automated email campaigns and skipped users</p>
          </div>

          <!-- Timeframe Toggle -->
          <div class="flex gap-2">
            <button
              @click="setDays(7)"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer',
                selectedDays === 7
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              ]">
              7 days
            </button>
            <button
              @click="setDays(14)"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer',
                selectedDays === 14
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              ]">
              14 days
            </button>
            <button
              @click="setDays(30)"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer',
                selectedDays === 30
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              ]">
              30 days
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto py-6 px-5">
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12 text-gray-400">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p class="text-sm">Loading campaign cycles...</p>
        </div>

        <!-- Cycles Table -->
        <div v-else-if="cycles.length > 0" class="overflow-x-auto">
          <table class="w-full border-collapse text-sm">
            <thead class="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th class="px-3 py-3 text-left font-semibold text-gray-700">Email Template</th>
                <th class="px-3 py-3 text-left font-semibold text-gray-700">Date</th>
                <th class="px-3 py-3 text-center font-semibold text-gray-700">Sent</th>
                <th class="px-3 py-3 text-center font-semibold text-gray-700">Skipped</th>
                <th class="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="cycle in cycles" :key="`${cycle.email_type}-${cycle.cycle_date}`">
                <!-- Cycle Row -->
                <tr class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td class="px-3 py-3">
                    <code class="bg-gray-100 px-2 py-1 rounded text-xs font-mono text-gray-800">
                      {{ cycle.email_type }}
                    </code>
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ formatDate(cycle.cycle_date) }}
                  </td>
                  <td class="px-3 py-3 text-center">
                    <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {{ cycle.sent_count }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-center">
                    <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {{ cycle.skipped_count }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-center">
                    <button
                      v-if="cycle.skipped_count > 0"
                      @click="toggleSkipped(cycle)"
                      class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer">
                      {{ isExpanded(cycle) ? 'Hide' : 'View' }} Skipped
                    </button>
                    <span v-else class="text-gray-400 text-xs">-</span>
                  </td>
                </tr>

                <!-- Expanded Skipped Emails Row -->
                <tr v-if="isExpanded(cycle)" class="bg-blue-50">
                  <td colspan="5" class="px-3 py-4">
                    <!-- Loading skipped emails -->
                    <div v-if="loadingSkipped[getCycleKey(cycle)]" class="text-center py-4 text-gray-500 text-sm">
                      Loading skipped emails...
                    </div>

                    <!-- Skipped emails list -->
                    <div v-else-if="skippedEmails[getCycleKey(cycle)]" class="space-y-2">
                      <h4 class="text-sm font-semibold text-gray-700 mb-3">Skipped Users:</h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        <div
                          v-for="(email, idx) in skippedEmails[getCycleKey(cycle)]"
                          :key="idx"
                          class="flex items-center gap-2 px-3 py-2 bg-white rounded border border-gray-200">
                          <span class="flex-1 text-sm text-gray-700 truncate">{{ email.user_email }}</span>
                          <span class="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600">
                            {{ email.user_locale || '-' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12 text-gray-400">
          <Mail :size="64" :stroke-width="1.5" class="mx-auto mb-4 opacity-50" />
          <p class="text-base">No email campaigns in the last {{ selectedDays }} days.</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { Mail } from 'lucide-vue-next'

export default {
  name: 'EmailReportsManager',
  components: {
    Mail
  },
  props: {
    indexPath: {
      type: String,
      required: true
    },
    initialCycles: {
      type: Array,
      default: () => []
    },
    initialSelectedDays: {
      type: Number,
      default: 7
    }
  },
  data() {
    return {
      cycles: this.initialCycles,
      selectedDays: this.initialSelectedDays,
      isLoading: false,
      expandedCycles: new Set(),
      skippedEmails: {},
      loadingSkipped: {}
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    getCycleKey(cycle) {
      return `${cycle.email_type}-${cycle.cycle_date}`
    },

    isExpanded(cycle) {
      return this.expandedCycles.has(this.getCycleKey(cycle))
    },

    async setDays(days) {
      this.selectedDays = days
      await this.fetchCycles()
    },

    async toggleSkipped(cycle) {
      const key = this.getCycleKey(cycle)

      if (this.isExpanded(cycle)) {
        // Collapse
        this.expandedCycles.delete(key)
      } else {
        // Expand and fetch if not already loaded
        this.expandedCycles.add(key)

        if (!this.skippedEmails[key]) {
          await this.fetchSkippedEmails(cycle)
        }
      }
    },

    async fetchCycles() {
      this.isLoading = true

      try {
        const params = new URLSearchParams({
          days: this.selectedDays.toString()
        })

        const response = await fetch(`${this.indexPath}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch cycles')
        }

        const data = await response.json()
        this.cycles = data.cycles
        this.selectedDays = data.selected_days

        // Clear expanded state when changing timeframe
        this.expandedCycles.clear()
        this.skippedEmails = {}

      } catch (error) {
        console.error('Failed to fetch email cycles:', error)
      } finally {
        this.isLoading = false
      }
    },

    async fetchSkippedEmails(cycle) {
      const key = this.getCycleKey(cycle)
      this.loadingSkipped[key] = true

      try {
        const params = new URLSearchParams({
          email_type: cycle.email_type,
          cycle_date: cycle.cycle_date
        })

        const response = await fetch(`${this.indexPath}/skipped_emails?${params}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch skipped emails')
        }

        const data = await response.json()
        this.skippedEmails[key] = data.skipped_emails

      } catch (error) {
        console.error('Failed to fetch skipped emails:', error)
      } finally {
        this.loadingSkipped[key] = false
      }
    }
  }
}
</script>
